import * as React from 'react';
import { FileRejection, useDropzone, Accept } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import style from './index.less';
import { MAX_FILE_SIZE } from '../CreateEducationalDocument';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { PropsWithChildren, useEffect } from 'react';

export interface DragDropProps {
  text: string;
  dragText: string;
  multiple?: boolean;
  accept?: Accept;
  maxFiles?: number;
  maxSize?: number;
  files?: File[];
  allowReplace?: boolean;
  height?: number;
  disableDelete?: boolean;
  onFileReject?: (files: FileRejection[]) => void;
  onFileDrop: (files: File[]) => void;
  onFileDelete?: (index: number, hasError: boolean) => void;
  onClear?: () => void;
  onBlur?: React.FocusEventHandler;
  inputProps?: React.HTMLProps<HTMLInputElement>;
  onError?: (hasError: boolean) => void;
}

const DragDrop = ({
  text,
  dragText,
  onFileDrop,
  accept = { 'application/pdf': ['.pdf'] },
  multiple = false,
  maxFiles = 1,
  maxSize = MAX_FILE_SIZE,
  files = [],
  allowReplace = false,
  height = 180,
  onFileDelete,
  onClear,
  inputProps,
  onError,
}: PropsWithChildren<DragDropProps>) => {
  // Filter out invalid files
  const validFiles = files.filter((file) => file instanceof File && file.size > 0);

  // Disable dropzone when allowReplace is false and files are already uploaded
  const isDropzoneDisabled = !allowReplace && validFiles.length > 0;

  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onFileDrop,
    multiple,
    accept,
    maxFiles,
    maxSize,
    disabled: isDropzoneDisabled,
  });

  // Check for errors and notify parent component
  useEffect(() => {
    const hasIndividualFileErrors = validFiles.some((file) => getFileErrors(file).length > 0);
    const hasError = validFiles.length > maxFiles || isDragReject || hasIndividualFileErrors;

    if (onError) {
      onError(hasError);
    }
  }, [validFiles.length, maxFiles, isDragReject, onError, validFiles]);

  // Helper function to get rejection errors for a specific file
  const getFileErrors = (file: File) => {
    const errors = [];

    // Check for errors from react-dropzone rejections
    const rejection = fileRejections.find((rejection) => rejection.file === file);
    if (rejection) {
      errors.push(...rejection.errors);
    }

    // Additional validation for files that might have been added previously
    if (!rejection) {
      // Check file size
      if (file.size > maxSize) {
        errors.push({
          code: 'file-too-large',
          message: `File is larger than ${(maxSize / 1000 / 1000).toFixed(
            maxSize % (1000 * 1000) === 0 ? 0 : 2,
          )}MB`,
        });
      }

      // Check file type
      const acceptedTypes = Object.keys(accept);
      const acceptedExtensions = Object.values(accept).flat();
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const mimeType = file.type;

      const isTypeAccepted =
        acceptedTypes.includes(mimeType) || acceptedExtensions.includes(fileExtension);

      if (!isTypeAccepted) {
        errors.push({
          code: 'file-invalid-type',
          message: `File type not accepted. Accepted types: ${acceptedExtensions.join(', ')}`,
        });
      }
    }

    return errors;
  };

  const rootProps = getRootProps();

  // Convert Accept object to display string
  const acceptedFormats = Object.values(accept).flat().join(', ').toUpperCase();

  // Helper function to format file size
  const formatFileSize = (bytes: number) => {
    const sizeMB = bytes / 1000 / 1000;
    return Number.isInteger(sizeMB) ? `${sizeMB}MB` : `${sizeMB.toFixed(2)}MB`;
  };

  // Handle file deletion
  const handleFileDelete = (index: number, hasError: boolean) => {
    if (onFileDelete) {
      onFileDelete(index, hasError);
    }
  };

  // Handle file download
  const handleFileDownload = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Determine the current state for styling and content
  const getDropzoneState = () => {
    if (validFiles.length > 0) return 'hasFiles'; // Always show hasFiles state when files are present
    if (isDragReject) return 'reject';
    if (isDragAccept) return 'accept';
    if (isDragActive) return 'active';
    return 'default';
  };

  const dropzoneState = getDropzoneState();

  const getIcon = (): IconProp => {
    if (dropzoneState === 'hasFiles') return ['fal', 'file-check'];
    if (dropzoneState === 'reject') return ['fal', 'file-times'];
    if (dropzoneState === 'accept') return ['fal', 'file-check'];

    return ['fal', 'file-upload'];
  };

  const getColor = (): string => {
    if (dropzoneState === 'hasFiles') return '#16ae82';
    if (dropzoneState === 'reject') return '#ef4444';
    if (dropzoneState === 'accept') return '#16ae82';

    return '#979797';
  };

  return (
    <div
      {...rootProps}
      className={`${style.root} ${style[dropzoneState]} ${
        validFiles.length > 0 ? style.hasFiles : ''
      }`}
      style={{ height: `${height}px` }}
    >
      <input
        {...inputProps}
        {...getInputProps()}
        onChange={async ({ currentTarget: { files } }) => {
          if (files && !isDropzoneDisabled) onFileDrop(Array.from(files));
        }}
      />
      {validFiles.length === 0 && (
        <div className={style.icon}>
          <FontAwesomeIcon icon={getIcon()} size="4x" color={getColor()} />
        </div>
      )}

      <div className={style.text}>
        {isDragReject ? (
          <p className={style.dragText} style={{ color: '#ef4444' }}>
            File type not supported
          </p>
        ) : isDragAccept ? (
          <p className={style.dragText} style={{ color: '#16ae82' }}>
            Release to upload
          </p>
        ) : isDragActive ? (
          <p className={style.dragText}>{dragText}</p>
        ) : validFiles.length > maxFiles ? (
          <div className={`${style.textContent} ${style.hasFiles}`}>
            <div className={`${style.mainText}`} style={{ color: '#ef4444' }}>
              Too many files! Please remove some files.
            </div>
            <div style={{ color: '#ef4444', fontSize: '0.9em' }}>
              Maximum allowed: {maxFiles} file{maxFiles !== 1 ? 's' : ''}
            </div>
            {onClear && (
              <button
                type="button"
                className={style.clearButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
              >
                Clear All
              </button>
            )}
            <div className={style.fileListContainer} style={{ maxHeight: `${height - 140}px` }}>
              {validFiles
                .slice()
                .reverse()
                .map((file, index) => {
                  const originalIndex = validFiles.length - 1 - index;
                  const fileErrors = getFileErrors(file);
                  const hasFileError = fileErrors.length > 0;

                  return (
                    <div
                      key={originalIndex}
                      className={`${style.fileInfo} ${hasFileError ? style.fileError : ''}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className={style.actionButtons}>
                        <button
                          className={style.downloadButton}
                          type="button"
                          aria-label="Download file"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileDownload(file);
                          }}
                        >
                          <FontAwesomeIcon icon={['far', 'download']} />
                        </button>
                        {((onFileDelete && allowReplace) || hasFileError) && (
                          <button
                            className={style.deleteButton}
                            type="button"
                            aria-label="Delete file"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileDelete(originalIndex, hasFileError);
                            }}
                          >
                            <FontAwesomeIcon icon={['far', 'trash']} />
                          </button>
                        )}
                      </div>
                      <div className={style.fileName}>{file.name}</div>
                      <div className={style.fileSize}>{formatFileSize(file.size)}</div>
                      {hasFileError && (
                        <div className={style.fileErrors}>
                          {fileErrors.map((error, errorIndex) => (
                            <div key={errorIndex} className={style.fileError}>
                              <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
                              <span>{error.message}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        ) : validFiles.length > 0 ? (
          <div className={`${style.textContent} ${style.hasFiles}`}>
            <div className={`${style.mainText} ${style.uploaded}`}>
              {validFiles.length === 1 ? 'File uploaded:' : `${validFiles.length} files uploaded:`}
            </div>
            {onClear && (
              <button
                type="button"
                className={style.clearButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
              >
                Clear All
              </button>
            )}
            <div className={style.fileListContainer} style={{ maxHeight: `${height - 140}px` }}>
              {validFiles
                .slice()
                .reverse()
                .map((file, index) => {
                  const originalIndex = validFiles.length - 1 - index;
                  const fileErrors = getFileErrors(file);
                  const hasFileError = fileErrors.length > 0;

                  return (
                    <div
                      key={originalIndex}
                      className={`${style.fileInfo} ${hasFileError ? style.fileError : ''}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className={style.actionButtons}>
                        <button
                          className={style.downloadButton}
                          type="button"
                          aria-label="Download file"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileDownload(file);
                          }}
                        >
                          <FontAwesomeIcon icon={['far', 'download']} />
                        </button>
                        {onFileDelete && (allowReplace || hasFileError) && (
                          <button
                            className={style.deleteButton}
                            type="button"
                            aria-label="Delete file"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileDelete(originalIndex, hasFileError);
                            }}
                          >
                            <FontAwesomeIcon icon={['far', 'trash']} />
                          </button>
                        )}
                      </div>
                      <div className={style.fileName}>{file.name}</div>
                      <div className={style.fileSize}>{formatFileSize(file.size)}</div>
                      {hasFileError && (
                        <div className={style.fileErrors}>
                          {fileErrors.map((error, errorIndex) => (
                            <div key={errorIndex} className={style.fileError}>
                              <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
                              <span>{error.message}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            {isDropzoneDisabled && (
              <div className={style.disabledMessage}>
                <p style={{ color: '#979797', fontSize: '0.9em', marginTop: '10px' }}>
                  File replacement is disabled
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className={style.textContent}>
            <div className={style.mainText}>
              {text} or <span className={style.chooseFile}>Choose file</span>
            </div>
            <div className={style.supportedFormats}>
              Supported formats: <span className={style.formatList}>{acceptedFormats}</span>
            </div>
            <div className={style.maxSize}>
              Maximum size:{' '}
              <span className={style.sizeValue}>
                {(() => {
                  const sizeMB = maxSize / 1000 / 1000;
                  return Number.isInteger(sizeMB) ? sizeMB : sizeMB.toFixed(2);
                })()}
                MB
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DragDrop;
